import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import ConfirmDialog from "../components/ConfirmDialog";
import ServiceBranchesModal from "../components/ServiceBranchesModal";
import ServiceCatalogueModal from "../components/ServiceCatalogueModal";
import Toast from "../components/Toast";
import { param, useListUrlState, useScrollRestoration, useValidOption, yesNo } from "../hooks/useUrlState";
import { apiErrorMessage } from "../services/api";
import {
  deleteCatalogueService,
  fetchCatalogue,
  fetchServiceCategories,
} from "../services/serviceService";

const CATALOGUE_FILTERS = {
  search: param.string("", { debounce: 400 }),
  category: param.string(),
  isActive: yesNo(),
};

/**
 * The business's service catalogue (BIZ-04): everything it sells, defined once.
 * Which branches offer a service, and what each charges, is set from the row's
 * own "branches" action rather than from every branch in turn.
 */
export default function ServiceCatalogue() {
  const { t, i18n } = useTranslation();

  const [services, setServices] = useState([]);
  const [meta, setMeta] = useState(null);
  const [categories, setCategories] = useState([]);

  const [loading, setLoading] = useState(true);

  // Page, filters and search live in the URL — see useUrlState.
  const { page, setPage, filters, applyFilters, searchTerm, setSearchTerm, submitSearch } =
    useListUrlState(CATALOGUE_FILTERS);
  useScrollRestoration(!loading);
  useValidOption(filters.category, categories.length ? categories : null, () =>
    applyFilters({ category: "" })
  );
  const [error, setError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [branchesFor, setBranchesFor] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { services: list, meta: pagination } = await fetchCatalogue({ page, filters });
      setServices(list);
      setMeta(pagination);
    } catch (err) {
      setError(apiErrorMessage(err, t("catalogue.loadFailed")));
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, [page, filters, t]);

  useEffect(() => {
    load();
  }, [load, i18n.language]);

  useEffect(() => {
    fetchServiceCategories()
      .then(setCategories)
      .catch(() => setCategories([]));
  }, [i18n.language]);

  const confirmDelete = async () => {
    setDeleteError("");
    setDeleteBusy(true);
    try {
      await deleteCatalogueService(deleting.id);
      setToast({ type: "success", message: t("catalogue.deleted", { name: deleting.name }) });
      setDeleting(null);
      load();
    } catch (err) {
      setDeleteError(apiErrorMessage(err, t("catalogue.deleteFailed")));
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">{t("catalogue.title")}</h1>
          <p className="text-sm text-ink-2">{t("catalogue.subtitle")}</p>
        </div>
        <div className="flex gap-2">
          <Link to="/service-categories" className="btn-secondary">
            {t("catalogue.manageCategories")}
          </Link>
          <button
            type="button"
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
            className="btn-primary"
          >
            {t("catalogue.new")}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
          {error}
        </div>
      )}

      <div className="card mb-4 grid gap-3 p-4 md:grid-cols-4">
        <div className="md:col-span-2">
          <label className="field-label" htmlFor="catalogue-search">
            {t("catalogue.filters.search")}
          </label>
          <input
            id="catalogue-search"
            className="field-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div>
          <label className="field-label" htmlFor="catalogue-category">
            {t("catalogue.filters.category")}
          </label>
          <select
            id="catalogue-category"
            className="field-input"
            value={filters.category}
            onChange={(e) => applyFilters({ category: e.target.value })}
          >
            <option value="">{t("catalogue.filters.any")}</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-end">
          <button
            type="button"
            className="btn-primary w-full"
            onClick={submitSearch}
          >
            {t("catalogue.filters.apply")}
          </button>
        </div>
      </div>

      <div className="card overflow-hidden">
        {loading ? (
          <p className="px-4 py-10 text-center text-sm text-ink-2">{t("common.loading")}</p>
        ) : services.length === 0 ? (
          <p className="px-4 py-12 text-center text-sm text-ink-2">{t("catalogue.empty")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-start text-sm">
              <thead className="border-b border-line bg-raised text-xs uppercase tracking-wide text-ink-2">
                <tr>
                  <th className="px-4 py-3">{t("catalogue.table.name")}</th>
                  <th className="px-4 py-3">{t("catalogue.table.category")}</th>
                  <th className="px-4 py-3">{t("catalogue.table.duration")}</th>
                  <th className="px-4 py-3">{t("catalogue.table.price")}</th>
                  <th className="px-4 py-3">{t("catalogue.table.branches")}</th>
                  <th className="px-4 py-3 text-end">{t("catalogue.table.actions")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {services.map((service) => (
                  <tr key={service.id}>
                    <td className="px-4 py-3">
                      <p className="font-medium">{service.name}</p>
                      {!service.is_active && (
                        <span className="text-xs text-ink-2">{t("catalogue.inactive")}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-ink-2">
                      {service.category?.name ?? t("common.none")}
                    </td>
                    <td className="px-4 py-3 text-ink-2" dir="ltr">
                      {/* The slot a booking occupies, buffers included. */}
                      {service.total_minutes} {t("catalogue.min")}
                    </td>
                    <td className="px-4 py-3 text-ink-2" dir="ltr">
                      {service.price}
                      {service.currency && (
                        <span className="ms-1 text-xs text-ink-2">{service.currency.code}</span>
                      )}
                      {/* "+ 15% tax" when tax is added on top of this price. */}
                      {service.tax_mode === "on_top" && Number(service.tax_rate) > 0 && (
                        <span className="block text-xs text-ink-2">
                          {t("catalogue.plusTax", { rate: Number(service.tax_rate) })}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-ink-2">{service.branches_count ?? 0}</td>
                    <td className="px-4 py-3 text-end">
                      <button
                        type="button"
                        onClick={() => setBranchesFor(service)}
                        className="text-sm font-semibold text-accent-text hover:underline"
                      >
                        {t("catalogue.branches")}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditing(service);
                          setFormOpen(true);
                        }}
                        className="ms-3 text-sm font-semibold text-accent-text hover:underline"
                      >
                        {t("common.edit")}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setDeleteError("");
                          setDeleting(service);
                        }}
                        className="ms-3 text-sm font-semibold text-danger-text hover:underline"
                      >
                        {t("common.delete")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {meta && meta.last_page > 1 && (
          <div className="flex items-center justify-between border-t border-line px-4 py-3 text-sm">
            <button
              type="button"
              className="btn-secondary"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              {t("common.previous")}
            </button>
            <span className="text-ink-2">
              {t("common.pageOf", { current: meta.current_page, last: meta.last_page })}
            </span>
            <button
              type="button"
              className="btn-secondary"
              disabled={page >= meta.last_page}
              onClick={() => setPage((p) => p + 1)}
            >
              {t("common.next")}
            </button>
          </div>
        )}
      </div>

      <ServiceCatalogueModal
        service={editing}
        categories={categories}
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={(saved, wasEditing) => {
          setFormOpen(false);
          setToast({
            type: "success",
            message: t(wasEditing ? "catalogue.updated" : "catalogue.created", { name: saved.name }),
          });
          load();
        }}
      />

      <ServiceBranchesModal
        service={branchesFor}
        open={Boolean(branchesFor)}
        onClose={() => setBranchesFor(null)}
        onSaved={() => {
          setBranchesFor(null);
          setToast({ type: "success", message: t("catalogue.branchesSaved") });
          load();
        }}
      />

      <ConfirmDialog
        open={Boolean(deleting)}
        title={t("catalogue.deleteTitle")}
        message={t("catalogue.deleteMessage", { name: deleting?.name ?? "" })}
        error={deleteError}
        busy={deleteBusy}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
      />

      {toast && <Toast type={toast.type} message={toast.message} onDone={() => setToast(null)} />}
    </div>
  );
}
